/**
 * Cancelar o agendamento no SISAC (Fase 3): os motivos da clínica e o envio.
 *
 * Os motivos vêm pela leitura genérica, como as listas do formulário do
 * agendamento; sem a operação ligada, o motivo vira texto (o código, como o
 * SISAC grava). O envio leva a versão da consulta que a tela leu, e uma chave
 * de idempotência por abertura do formulário: o segundo clique, ou o reenvio
 * depois de uma resposta perdida, devolve o primeiro cancelamento.
 */

import { useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ErroDaApi, type ConsultaDaOportunidade } from '@cliqcrm/contracts';
import { useApi } from '@/shared/api/consultas';
import { chaves } from '@/shared/api/chaves';
import { recadoDeErro } from '@/shared/api/recado-de-erro';
import type { EstadoDoEnvio, ListaDoFormulario } from '../lib/consultas-na-tela';
import { useListaDaAgenda } from './usar-nova-consulta';

export interface PedidoDeCancelamento {
  readonly motivo: string;
  readonly filaDeEspera: boolean;
  readonly observacao: string;
}

const novaChave = () =>
  globalThis.crypto?.randomUUID?.() ??
  `cancelar-${Date.now()}-${Math.random().toString(16).slice(2)}`;

export function useCancelamento({
  oportunidadeId,
  consulta,
  operacoes,
  aoCancelar,
}: {
  oportunidadeId: string;
  consulta: ConsultaDaOportunidade;
  operacoes: { readonly liberar: string; readonly motivos?: string };
  aoCancelar: () => void;
}) {
  const api = useApi();
  const cache = useQueryClient();
  const chave = useRef(novaChave());
  const leitura = useListaDaAgenda(
    oportunidadeId,
    operacoes.motivos,
    'motivos_de_cancelamento',
    {},
  );
  const motivos: ListaDoFormulario =
    operacoes.motivos === undefined
      ? { situacao: 'desligada' }
      : leitura.isError
        ? {
            situacao: 'erro',
            recado: recadoDeErro(leitura.error, { generica: 'Não foi possível ler os motivos.' }),
          }
        : leitura.data
          ? { situacao: 'pronta', opcoes: leitura.data }
          : { situacao: 'carregando' };

  const cancelar = useMutation({
    mutationFn: async (pedido: PedidoDeCancelamento) => {
      if (!api) throw new Error('Sem sessão.');
      // Só a consulta marcada se cancela, e ela tem versão; sem, não há o que mandar.
      if (consulta.versao === null) throw new Error('A consulta ainda não foi gravada.');
      const versao = consulta.versao;
      const observacao = pedido.observacao.trim();
      return api.cancelarConsulta({
        caminho: { id: oportunidadeId },
        corpo: {
          operacao_id: operacoes.liberar,
          motivo: pedido.motivo,
          fila_de_espera: pedido.filaDeEspera,
          ...(observacao ? { observacao } : {}),
          versao,
        },
        idempotencyKey: chave.current,
      });
    },
    onSuccess: (cancelada) => {
      cache.setQueryData(chaves.consultaDaOportunidade(oportunidadeId), cancelada);
      aoCancelar();
    },
    onError: (erro) => {
      // Mudou no CRM ou no SISAC: a tela relê a consulta para mostrar o que ficou.
      if (erro instanceof ErroDaApi && erro.status === 409) {
        void cache.invalidateQueries({ queryKey: chaves.consultaDaOportunidade(oportunidadeId) });
      }
      // Com outro corpo, a mesma chave seria recusada: a próxima tentativa leva uma nova.
      if (erro instanceof ErroDaApi && erro.codigo === 'IDEMPOTENCIA_DIVERGENTE') {
        chave.current = novaChave();
      }
    },
  });

  const envio: EstadoDoEnvio = cancelar.isPending
    ? { situacao: 'enviando' }
    : !cancelar.isError
      ? { situacao: 'parado' }
      : cancelar.error instanceof ErroDaApi && cancelar.error.status === 503
        ? { situacao: 'fora-do-ar' }
        : cancelar.error instanceof ErroDaApi && cancelar.error.codigo === 'CONFLITO_DE_VERSAO'
          ? { situacao: 'conflito' }
          : {
              situacao: 'erro',
              recado: recadoDeErro(cancelar.error, {
                generica: 'Não foi possível cancelar no SISAC.',
              }),
            };

  return { motivos, envio, cancelar: (pedido: PedidoDeCancelamento) => cancelar.mutate(pedido) };
}
